import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Check, Crown, Building2, Mail } from "lucide-react";
import { PayPalBuy, PAYPAL_OPTIONS } from "@/components/PayPalBuy";
import { signInWithGoogle } from "@/lib/google";

const Feature = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-gray-600">
    <Check size={15} className="text-[#C0392B] mt-0.5 shrink-0" /> {children}
  </li>
);

export const PricingSection = ({ clientStatus, onStatusChange, googleUser }) => {
  const isPro = clientStatus?.is_pro;

  return (
    <PayPalScriptProvider options={PAYPAL_OPTIONS}>
      <section id="pricing" className="w-full mt-20" data-testid="pricing-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="pricing-heading">
          Plans & Credits
        </h2>
        <p className="text-sm text-gray-500 mb-8">1 credit = 1 domain scanned. Free credits reset every day.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm flex flex-col" data-testid="plan-free">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Free</p>
            <p className="text-3xl font-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>$0</p>
            <ul className="space-y-2 flex-1">
              <Feature>25 credits per day</Feature>
              <Feature>Single & Bulk scanning</Feature>
              <Feature>Compare & CSV export</Feature>
            </ul>
            <div className="mt-5 py-2 text-center text-sm font-medium text-gray-400 border border-dashed border-gray-200 rounded-full">Current plan</div>
          </div>

          <div className={`bg-white rounded-xl p-6 shadow-sm flex flex-col relative ${isPro ? "border-2 border-green-500" : "border-2 border-[#C0392B]"}`} data-testid="plan-pro">
            <span className="absolute -top-3 left-5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#C0392B] text-white">MOST POPULAR</span>
            <p className="text-xs font-semibold text-[#C0392B] uppercase tracking-wider mb-1 flex items-center gap-1"><Crown size={13} /> PRO</p>
            <p className="text-3xl font-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>$10 <span className="text-sm font-normal text-gray-400">one-time</span></p>
            <ul className="space-y-2 flex-1">
              <Feature>Unlocks the Filter PRO feature</Feature>
              <Feature>500 credits per day (20× free)</Feature>
              <Feature>Priority everything, forever</Feature>
            </ul>
            <div className="mt-5">
              {isPro ? (
                <div className="py-2 text-center text-sm font-bold text-green-700 bg-green-50 rounded-full" data-testid="pro-active-badge">✓ PRO Active</div>
              ) : googleUser ? (
                <div>
                  <p className="text-xs text-gray-400 mb-2 truncate text-center" data-testid="pro-signed-in-as">Signed in as {googleUser.email}</p>
                  <PayPalBuy product="pro" onSuccess={onStatusChange} />
                </div>
              ) : (
                <button
                  data-testid="google-signin-btn"
                  onClick={signInWithGoogle}
                  className="w-full py-2.5 text-sm font-bold bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Sign in with Google to buy
                </button>
              )}
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 shadow-sm flex flex-col text-white" data-testid="plan-enterprise">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Building2 size={13} /> Enterprise</p>
            <p className="text-3xl font-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Custom</p>
            <ul className="space-y-2 flex-1 text-sm text-gray-300">
              <li className="flex items-start gap-2"><Check size={15} className="text-[#E74C3C] mt-0.5 shrink-0" /> Unlimited scanning & API access</li>
              <li className="flex items-start gap-2"><Check size={15} className="text-[#E74C3C] mt-0.5 shrink-0" /> Dedicated support & SLA</li>
              <li className="flex items-start gap-2"><Check size={15} className="text-[#E74C3C] mt-0.5 shrink-0" /> Volume pricing</li>
            </ul>
            <a
              href="mailto:sales@wraithchecker.io?subject=Enterprise%20Inquiry"
              data-testid="enterprise-contact-btn"
              className="mt-5 py-2.5 text-center text-sm font-bold bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5"
            >
              <Mail size={14} /> Contact Us
            </a>
          </div>
        </div>
      </section>
    </PayPalScriptProvider>
  );
};
